'use client';

import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import IndustryCrossLinks from "@/components/landing/IndustryCrossLinks";
import ResearchBacking from "@/components/landing/ResearchBacking";
import Footer from "@/components/shared/Footer";
import PublicHeader from "@/components/shared/PublicHeader";

const fitnessResearch = [
  {
    text: "The average gym loses 50% of new members within 6 months. Each lost member costs $500–$1,800 in lifetime value.",
    source: "IHRSA Industry Report 2024",
    url: "https://www.ihrsa.org/publications/",
  },
  {
    text: "Personalized re-engagement messages recover up to 22% of at-risk members who haven&apos;t visited in 14+ days.",
    source: "Club Industry Member Retention Study 2024",
    url: "https://www.clubindustry.com/",
  },
  {
    text: "Gyms using automated outreach to inactive members reduce monthly churn by 18-25% compared to manual follow-up.",
    source: "Health & Fitness Association (HFA) 2025 Report",
    url: "https://www.healthandfitness.org/the-2025-hfa-global-report/",
  },
];

const fitnessFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an AI retention agent for fitness studios?",
      acceptedAnswer: { "@type": "Answer", text: "An AI retention agent for fitness studios is a chatbot that monitors member activity and automatically reaches out to at-risk members with personalised re-engagement messages. It handles class bookings, answers FAQs about schedules and memberships, and helps reduce churn." },
    },
    {
      "@type": "Question",
      name: "How does AI member retention work for gyms?",
      acceptedAnswer: { "@type": "Answer", text: "The AI uses retrieval-augmented generation (RAG) grounded in your gym's class schedules, membership plans, and business information. It identifies members showing signs of disengagement and sends targeted messages to bring them back. It only shares information you have explicitly provided." },
    },
    {
      "@type": "Question",
      name: "What are the limitations of AI agents for fitness businesses?",
      acceptedAnswer: { "@type": "Answer", text: "AI agents cannot provide personal training advice, medical guidance, or nutrition plans. They are designed for structured tasks like class bookings, membership enquiries, and re-engagement messaging. Health-related questions are always directed to your qualified staff." },
    },
  ],
};

const fitnessSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aditya Labs AI Retention Agent for Fitness Studios",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "AI agent that identifies at-risk members, sends personalised re-engagement messages, and reduces churn automatically.",
  url: "https://adityalabs.ai/fitness",
  audience: { "@type": "BusinessAudience", audienceType: "Fitness Studios and Gyms" },
  provider: { "@type": "Organization", name: "Aditya Labs", url: "https://adityalabs.ai" },
  offers: { "@type": "Offer", price: "49", priceCurrency: "USD", description: "Plans from $49/month" },
};

export default function FitnessLandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans overflow-x-hidden selection:bg-lime-500/30">
      <JsonLd data={fitnessSchema} />
      <JsonLd data={fitnessFaqSchema} />

      {/* NAVBAR */}
      <PublicHeader variant="industry" accentColor="lime" maxWidth="max-w-7xl" />

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">

        {/* Ambient Background Glow (Neon Lime for Energy) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-lime-500/10 rounded-full blur-[120px] -z-10 opacity-60"></div>

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT COLUMN: COPY */}
          <div className="space-y-10 relative z-10">

            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-800 border border-gray-700 text-gray-300 text-xs font-bold tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              Churn Alert: 15% of members are &ldquo;At Risk&rdquo;
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Half Your Members <br />
              Will Quit Within <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-green-500">Six Months.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-gray-400 max-w-lg leading-relaxed">
              Don&apos;t let them fade away. Our AI identifies members who stop visiting and texts them personalised offers to get them back in the gym — automatically.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/signup" className="flex items-center justify-center bg-lime-500 hover:bg-lime-400 text-black px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-lime-900/20 transition-all transform hover:-translate-y-1">
                Stop The Churn
              </Link>
              <Link href="/demo" className="flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium backdrop-blur-sm transition-all">
                See Retention Bot
              </Link>
            </div>

            {/* Trust/Guarantee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-500 dark:text-gray-400 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-lime-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                <span className="font-medium text-gray-300">Reads Check-In Data from Mindbody &amp; Glofox</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-lime-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">Churn Reduction Focus</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-md">AI re-engages at-risk members before they cancel. Retaining just 3 members/month at $150 average = $450 saved vs. plans starting at $49/mo. We are onboarding our first fitness studios now.</p>
            </div>
          </div>

          {/* RIGHT COLUMN: VISUAL PROOF (Re-engagement Automation) */}
          <div className="relative mx-auto lg:mx-0 w-full max-w-md">

            {/* Glass Container */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-[2.5rem] border border-gray-700/50 shadow-2xl backdrop-blur-xl relative z-10">
              <div className="bg-black rounded-[2rem] overflow-hidden border border-gray-800 h-[650px] flex flex-col relative">

                {/* Status Bar */}
                <div className="bg-gray-900/80 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lime-500 to-green-600 flex items-center justify-center text-xs font-bold text-black shadow-lg">AI</div>
                    <div>
                      <div className="font-semibold text-white text-sm">Coach Mike (AI)</div>
                      <div className="text-[10px] text-lime-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-lime-400 animate-pulse"></span>
                        Re-engaging inactive members
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 p-5 space-y-6 overflow-y-auto bg-gray-950 scrollbar-hide">
                  <div className="text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest my-4">Inactive for 14 Days</div>

                  {/* Message 1 (AI Outreach) */}
                  <div className="flex justify-start">
                    <div className="bg-lime-600 text-black px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm font-medium leading-relaxed shadow-lg shadow-lime-900/20">
                      Hey Alex! We missed you at the 6am class. Everything okay? We have a spot open tomorrow if you want to crush it? 💪
                    </div>
                  </div>

                  {/* Message 2 (Member) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Yeah, work&apos;s been crazy. I&apos;ve been slacking honestly.
                    </div>
                  </div>

                  {/* Message 3 (AI Motivation) */}
                  <div className="flex justify-start">
                    <div className="bg-lime-600 text-black px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm font-medium leading-relaxed shadow-lg shadow-lime-900/20">
                      It happens! Come in tomorrow and I&apos;ll give you a free smoothie on the house. Let&apos;s get that momentum back. You in?
                    </div>
                  </div>

                  {/* Message 4 (Member) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Deal. Book me for 6am.
                    </div>
                  </div>

                  {/* Retention Notification */}
                  <div className="flex justify-center my-6">
                    <div className="bg-lime-500/10 text-lime-400 text-xs font-semibold px-4 py-1.5 rounded-full border border-lime-500/20 flex items-center gap-2">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                      Member Retained ($150/mo saved)
                    </div>
                  </div>
                </div>

                {/* Input Area (Visual) */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="h-12 bg-gray-800 rounded-full w-full opacity-50 border border-gray-700"></div>
                </div>
              </div>
            </div>

            {/* Back Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-lime-500/20 blur-[90px] -z-10 rounded-full"></div>
          </div>
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What Gym Owners Are Saying</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-yellow-400 text-sm">★★★★★</span>
              </div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-4">
                &quot;We were losing 22 members per month. After deploying the AI retention agent, our monthly churn dropped from 8% to 5.2% in 60 days. That&apos;s $2,500/month in saved revenue.&quot;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-lime-500 to-green-600 flex items-center justify-center text-black text-xs font-bold">TB</div>
                <div>
                  <p className="text-sm font-semibold text-white">Tyler B.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Owner, Boutique Fitness Studio, Denver CO</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-yellow-400 text-sm">★★★★★</span>
              </div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-4">
                &quot;I was spending 10 hours a week texting inactive members manually. The AI does it automatically from my Mindbody data and the tone is perfect — casual, motivational, not pushy.&quot;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">AW</div>
                <div>
                  <p className="text-sm font-semibold text-white">Alicia W.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Owner, CrossFit Affiliate, Portland OR</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-10 px-6">
        <div className="max-w-2xl mx-auto text-center bg-lime-900/20 border border-lime-700/30 rounded-2xl p-8">
          <p className="text-white font-semibold text-lg mb-2">Plans start at $49/month</p>
          <p className="text-gray-400 text-sm mb-4">Retain just 1 member and your subscription pays for itself 3x over.</p>
          <Link href="/pricing" className="text-lime-400 hover:text-lime-300 text-sm font-medium underline">View all pricing plans →</Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[...fitnessFaqSchema.mainEntity,
              { name: "Can it handle class bookings too?", acceptedAnswer: { text: "Yes. When members message the AI asking about class availability, it can share your class schedule, check for open spots, and book them in. You upload your weekly schedule to the knowledge base and the AI handles the rest. Members can ask things like 'Is there a 6am spin class tomorrow?' and get an instant answer." } },
              { name: "What happens if a member responds negatively?", acceptedAnswer: { text: "The AI is trained to be empathetic, not pushy. If a member says they want to cancel or expresses frustration, the AI acknowledges their feelings, offers a one-time incentive if you have configured one (like a free session or freeze option), and escalates to your team if the member insists. You get a notification immediately so you can follow up personally." } },
            ].map((faq: { name: string; acceptedAnswer: { text: string } }, i: number) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacking stats={fitnessResearch} />
      <IndustryCrossLinks current="fitness" />
      <Footer />
    </div>
  );
}
